import type { QuotasData } from '@stw/types';

export type CreditType = 'scan' | 'annotation' | 'embed';

/**
 * Calculate quota display string in x/y (+z) format with topup support
 * @param quotas - Quota object from API (matches backend getCreditsAndQuotas structure)
 * @param type - Type of credit to display ('scan', 'annotation', 'embed')
 * @returns Formatted quota display string
 */
export const calculateQuotaDisplay = (
  quotas: QuotasData | null,
  type: CreditType = 'scan'
): string => {
  if (!quotas) return '0/0 (+0)';

  if (type === 'scan') {
    // scan_credit = remaining plan credits, topup_scan_credit = remaining topup credits
    const planAvailable = quotas.scan_credit || 0;
    const planQuota = quotas.scan_quota || 0;
    const topupAvailable = quotas.topup_scan_credit || 0;
    return `${planAvailable}/${planQuota} (+${topupAvailable})`;
  }

  if (type === 'annotation') {
    // Annotations are per-model quota, not consumed credits
    const annotationQuota = quotas.annotation_quota || 0;
    return `${annotationQuota} per model`;
  }

  if (type === 'embed') {
    // embed_credit = remaining available embeds from backend
    const availableEmbeds = quotas.embed_credit || 0;
    const totalEmbeds = quotas.embed_quota || 0;
    return `${availableEmbeds}/${totalEmbeds}`;
  }

  return '0/0 (+0)';
};

/**
 * Check if user can perform an action based on available quota
 * @param quotas - Quota object from API
 * @param type - Type of credit to check ('scan', 'annotation', 'embed')
 * @returns True if user has available quota
 */
export const hasAvailableQuota = (
  quotas: QuotasData | null,
  type: CreditType = 'scan'
): boolean => {
  if (!quotas) return false;

  if (type === 'scan') {
    // total_scan_credit is the pre-calculated total (plan + topup) from backend
    return (quotas.total_scan_credit || 0) > 0;
  }

  if (type === 'annotation') {
    // Annotations are per-model, so check if quota exists
    const annotationQuota = quotas.annotation_quota || 0;
    return annotationQuota > 0;
  }

  if (type === 'embed') {
    // embed_credit is remaining available embeds
    const availableEmbeds = quotas.embed_credit || 0;
    return availableEmbeds > 0;
  }

  return false;
};

/**
 * Get total available credits for a specific type
 * @param quotas - Quota object from API
 * @param type - Type of credit to calculate
 * @returns Total available credits (plan + topup)
 */
export const getTotalAvailableCredits = (
  quotas: QuotasData | null,
  type: CreditType = 'scan'
): number => {
  if (!quotas) return 0;

  if (type === 'scan') {
    // total_scan_credit is pre-calculated from backend
    return quotas.total_scan_credit || 0;
  }

  if (type === 'annotation') {
    const annotationQuota = quotas.annotation_quota || 0;
    return annotationQuota;
  }

  if (type === 'embed') {
    const availableEmbeds = quotas.embed_credit || 0;
    return availableEmbeds;
  }

  return 0;
};

/**
 * Get breakdown of available credits by source
 * @param quotas - Quota object from API
 * @param type - Type of credit to analyze
 * @returns Object with plan and topup credit breakdown
 */
export const getCreditBreakdown = (
  quotas: QuotasData | null,
  type: CreditType = 'scan'
) => {
  if (!quotas) return { plan: 0, topup: 0, total: 0 };

  if (type === 'scan') {
    // scan_credit = plan credits, topup_scan_credit = topup credits
    const plan = quotas.scan_credit || 0;
    const topup = quotas.topup_scan_credit || 0;
    const total = quotas.total_scan_credit || 0;
    return { plan, topup, total };
  }

  if (type === 'annotation') {
    const plan = quotas.annotation_quota || 0;
    const topup = 0; // No topup annotations currently
    return { plan, topup, total: plan + topup };
  }

  if (type === 'embed') {
    const plan = quotas.embed_credit || 0;
    const topup = 0; // No topup embeds currently
    return { plan, topup, total: plan + topup };
  }

  return { plan: 0, topup: 0, total: 0 };
};
